import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ThumbsUp, ThumbsDown, Send } from "lucide-react";
import FeedbackService from "../../services/Feedback";

/**
 * A reusable dialog component for collecting user feedback on various document types
 *
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {Function} props.onClose Callback when dialog is closed
 * @param {string} props.userId Current user ID
 * @param {string} props.projectId Current project ID
 * @param {string} props.documentId ID of the document being rated
 * @param {string} props.documentType Type of document (icp, adstrategy, adinsights, competitiveanalysis)
 * @param {string} props.documentName Name of the document for display purposes
 * @returns {JSX.Element} The SendFeedbackDialog component
 */
const SendFeedbackDialog = ({
  open,
  onClose,
  userId,
  projectId,
  documentId,
  documentType,
  documentName,
}: any) => {
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [feedbackContext, setFeedbackContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (selectedFeedback === null) {
      setError("Please select positive or negative feedback");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const feedbackData = {
        userId,
        documentId,
        documentType: documentType.toLowerCase(),
        feedback: selectedFeedback === "positive", // true for positive, false for negative
        feedbackContext: feedbackContext.trim() || undefined,
      };

      const feedbackService = new FeedbackService();
      const response = await feedbackService.storeFeedback(
        projectId,
        feedbackData
      );

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setError("Failed to submit feedback. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError(
        "An error occurred while submitting feedback. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setSelectedFeedback(null);
    setFeedbackContext("");
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleFeedbackSelection = (type: any) => {
    setSelectedFeedback(type);
    setError(null);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={loading}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          width: "100%",
          maxWidth: "450px",
          p: 2,
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: 600, fontSize: "1.25rem", color: "#111827", pb: 1 }}
      >
        Send Feedback
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Feedback submitted successfully!
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography sx={{ mb: 2, color: "#4B5563", fontSize: "0.95rem" }}>
              How would you rate the generated "{documentName}" document?
            </Typography>

            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 3, mb: 3 }}
            >
              <Button
                variant={
                  selectedFeedback === "positive" ? "contained" : "outlined"
                }
                color={selectedFeedback === "positive" ? "success" : "inherit"}
                onClick={() => handleFeedbackSelection("positive")}
                startIcon={<ThumbsUp size={18} />}
                sx={{
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  borderColor:
                    selectedFeedback === "positive" ? "transparent" : "#D1D5DB",
                  "&:hover": {
                    backgroundColor:
                      selectedFeedback === "positive"
                        ? "#2E7D32"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                disabled={loading}
              >
                Positive
              </Button>

              <Button
                variant={
                  selectedFeedback === "negative" ? "contained" : "outlined"
                }
                color={selectedFeedback === "negative" ? "error" : "inherit"}
                onClick={() => handleFeedbackSelection("negative")}
                startIcon={<ThumbsDown size={18} />}
                sx={{
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  borderColor:
                    selectedFeedback === "negative" ? "transparent" : "#D1D5DB",
                  "&:hover": {
                    backgroundColor:
                      selectedFeedback === "negative"
                        ? "#C62828"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                disabled={loading}
              >
                Negative
              </Button>
            </Box>

            <TextField
              label="Additional Comments (Optional)"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              value={feedbackContext}
              onChange={(e) => setFeedbackContext(e.target.value)}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.15)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(0, 0, 0, 0.25)",
                  },
                },
              }}
            />
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderColor: "#D1D5DB",
              color: "#374151",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
              },
              textTransform: "none",
            }}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || selectedFeedback === null}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Send size={16} />
              )
            }
            sx={{
              backgroundColor: "#E11D48",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "0.875rem",
              fontWeight: 500,
              boxShadow: "0 1px 3px rgba(225, 29, 72, 0.2)",
              "&:hover": {
                backgroundColor: "#BE123C",
                boxShadow: "0 2px 6px rgba(225, 29, 72, 0.3)",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(225, 29, 72, 0.5)",
                color: "rgba(255, 255, 255, 0.6)",
              },
              textTransform: "none",
            }}
          >
            {loading ? "Submitting..." : "Submit Feedback"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SendFeedbackDialog;
